// Notification service for handling delegation notifications
class NotificationService {
  constructor() {
    this.notifications = [];
    this.listeners = [];
  }

  // Add a listener for notification updates
  addListener(callback) {
    this.listeners.push(callback);
  }

  // Remove a listener
  removeListener(callback) {
    const index = this.listeners.indexOf(callback);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  // Notify all listeners of changes
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.notifications));
  }

  // Create a new notification
  createNotification({ type, title, message, nodeId, contact, priority = 'medium' }) {
    const notification = {
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type, // 'delegation', 'completion', 'error', etc.
      title,
      message,
      nodeId,
      contact,
      priority,
      timestamp: new Date().toISOString(),
      read: false,
      actions: []
    };

    this.notifications.unshift(notification);
    this.notifyListeners();
    return notification;
  }

  // Mark a notification as read
  markAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.notifyListeners();
    }
  }

  // Mark all notifications as read
  markAllAsRead() {
    this.notifications.forEach(notification => {
      notification.read = true;
    });
    this.notifyListeners();
  }

  // Remove a notification
  removeNotification(notificationId) {
    this.notifications = this.notifications.filter(n => n.id !== notificationId);
    this.notifyListeners();
  }

  // Get unread notifications count
  getUnreadCount() {
    return this.notifications.filter(n => !n.read).length;
  }

  // Get all notifications
  getNotifications() {
    return [...this.notifications];
  }

  // Get notifications for a specific contact
  getNotificationsForContact(contactId) {
    return this.notifications.filter(n => 
      n.contact && n.contact.id === contactId
    );
  }

  // Simulate sending an email notification
  async sendEmailNotification(contact, subject, body) {
    console.log(`[NotificationService] Sending email to ${contact.email}: ${subject}`);
    // In a real implementation, this would integrate with an email service
    // For now, we'll just simulate the delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return { success: true, messageId: `msg_${Date.now()}` };
  }

  // Simulate sending an in-app notification
  async sendInAppNotification(contact, title, message, nodeId) {
    console.log(`[NotificationService] Sending in-app notification to ${contact.name}: ${title}`);
    const notification = this.createNotification({
      type: 'delegation',
      title,
      message,
      nodeId,
      contact,
      priority: 'high'
    });
    
    // In a real implementation, this might integrate with a WebSocket service
    // or push notification system
    return notification;
  }

  // Send delegation notification to a human contact
  async sendDelegationNotification(node, contact, instructions) {
    const title = `Task Delegation: ${node.label}`;
    const message = instructions || `You have been delegated the task "${node.label}" in a workflow. Please review and complete when possible.`;
    
    // Send both email and in-app notification
    const emailPromise = this.sendEmailNotification(
      contact,
      title,
      `${message}\n\nTask Details:\n- Workflow Node: ${node.label}\n- Node Type: ${node.type}\n- Delegated by: System`
    );
    
    const inAppPromise = this.sendInAppNotification(
      contact,
      title,
      message,
      node.id
    );
    
    const [emailResult, inAppResult] = await Promise.all([emailPromise, inAppPromise]);
    return { emailResult, inAppResult };
  }

  // Send completion notification
  async sendCompletionNotification(node, originalContact, resultMessage) {
    if (!originalContact) return;
    
    const title = `Task Completed: ${node.label}`;
    const message = resultMessage || `The delegated task "${node.label}" has been completed.`;
    
    await this.sendEmailNotification(
      originalContact,
      title,
      `${message}\n\nTask Details:\n- Workflow Node: ${node.label}\n- Completed at: ${new Date().toLocaleString()}`
    );
    
    this.sendInAppNotification(
      originalContact,
      title,
      message,
      node.id
    );
  }
}

// Create a singleton instance
const notificationService = new NotificationService();

export default notificationService;